/**
 * Persistent launcher wallpaper storage.
 * Uses localStorage for small data URLs, IndexedDB when localStorage quota is exceeded.
 */

const IDB_NAME = 'signalbridge-launcher';
const IDB_VERSION = 1;
const IDB_STORE = 'backgrounds';
const IDB_MARKER = '__idb__';

export const getBackgroundStorageKey = (uid) => (uid ? `launcher_background_${uid}` : null);

const openDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('IndexedDB is not available'));
    return;
  }
  const request = indexedDB.open(IDB_NAME, IDB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(IDB_STORE)) {
      db.createObjectStore(IDB_STORE);
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Could not open background storage'));
});

const idbGet = async (key) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const request = store.get(key);
      request.onsuccess = () => resolve(typeof request.result === 'string' ? request.result : '');
      request.onerror = () => reject(request.error || new Error('Could not read background'));
    });
  } finally {
    db.close();
  }
};

const idbSet = async (key, value) => {
  const db = await openDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const request = store.put(value, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error || new Error('Could not save background'));
    });
  } finally {
    db.close();
  }
};

const idbDelete = async (key) => {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await openDb();
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        const store = tx.objectStore(IDB_STORE);
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } finally {
      db.close();
    }
  } catch {
    // non-fatal
  }
};

export const readLauncherBackgroundUrlAsync = async (uid) => {
  const key = getBackgroundStorageKey(uid);
  if (!key) return '';
  try {
    const marker = localStorage.getItem(key);
    if (!marker) return '';
    if (marker === IDB_MARKER) return idbGet(key);
    return marker;
  } catch {
    return '';
  }
};

export const writeLauncherBackgroundUrlAsync = async (uid, url) => {
  const key = getBackgroundStorageKey(uid);
  if (!key) return;
  if (!url) {
    localStorage.removeItem(key);
    await idbDelete(key);
    return;
  }
  try {
    localStorage.setItem(key, url);
    await idbDelete(key);
    return;
  } catch {
    // fall through to IndexedDB
  }
  try {
    await idbSet(key, url);
    localStorage.setItem(key, IDB_MARKER);
  } catch {
    throw new Error('Could not save the image on this device. Try a smaller photo.');
  }
};

/** Sync read for legacy call sites — only returns values still in localStorage. */
export const readLauncherBackgroundUrlSync = (uid) => {
  const key = getBackgroundStorageKey(uid);
  if (!key) return '';
  try {
    const value = localStorage.getItem(key);
    if (!value || value === IDB_MARKER) return '';
    return value;
  } catch {
    return '';
  }
};
